import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box, BoxProps, Button, Body, Icon } from '@hover/blueprint';
import { iFile, iX } from '@hover/icons';

const StyledInput = styled.input`
  position: absolute;
  top: 0;
  z-index: -1;
`;

interface Props extends BoxProps {
  title: string;
  label: string;
  onRemove: () => void;
  onChangeFile: (value: File) => void;
  errorMessage: string | undefined;
  value?: File;
}

const FileUploader: React.FC<Props> = ({
  title,
  label,
  onRemove,
  onChangeFile,
  errorMessage,
  value,
  ...rest
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedFile(value ?? null);
  }, [value]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    if (file) {
      setSelectedFile(file);
      onChangeFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <Box w="100%" position="relative" {...rest} flexDir="column">
      {selectedFile ? (
        <Box flexDir="column">
          <Body
            textTransform="uppercase"
            size={300}
            isBold
            letterSpacing="0.15rem"
            mb="10px"
          >
            {label}
          </Body>
          <Box alignItems="center">
            <Box minW={200} maxW={200} mr={200}>
              <Icon color="brandNavy.500" size="default" icon={iFile} m={0} />
            </Box>
            <Body
              overflow="hidden"
              mr={400}
              size={400}
              maxW="50%"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {selectedFile.name}
            </Body>
            <Box minW={200} maxW={200} mr={200} onClick={removeFile}>
              <Icon
                strokeWidth="2px"
                size="small"
                icon={iX}
                m={0}
                color="primary.600"
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <StyledInput
            accept=".pdf, .doc, .docx, .txt, .rtf)"
            type="file"
            hidden
            ref={inputRef}
            onChange={onChange}
          />
          <Button
            borderWidth="2px"
            w="100%"
            fill="outline"
            size="large"
            onClick={focusInput}
            textTransform="uppercase"
          >
            {title}
          </Button>
          {!!errorMessage && (
            <Body mt={200} ml={200} size={300} isSemiBold color="danger.300">
              {errorMessage}
            </Body>
          )}
        </>
      )}
    </Box>
  );
};

export default FileUploader;
